import { Component, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { catchError, EMPTY, filter, finalize, interval, Subject, takeUntil } from 'rxjs';
import { TestResult, TestResultAction } from '../../../shared/models/test-result.model';
import { DlrRecStatusRenderUtils } from '../../../shared/services/dlr-rec-status-render-utils';
import { NotificationService } from '../../../shared/services/notification.service';
import { PhoneReportService } from '../../../shared/services/phone-report.service';
import { TestResultUtils } from '../../../shared/services/test-result-utils';

@Component({
  standalone: false,
  selector: 'app-phone-reports-test-result-table',
  templateUrl: './phone-reports-test-result-table.component.html',
  styleUrls: ['./phone-reports-test-result-table.component.scss'],
})
export class PhoneReportTestResultTableComponent implements OnInit, OnDestroy {
  readonly reportId = input<number>();

  readonly phoneReportService = inject(PhoneReportService);
  readonly notificationService = inject(NotificationService);

  private destroy$ = new Subject<void>();

  readonly loading = signal<boolean>(false);
  readonly rowData = signal<TestResult[]>([]);

  infoExpanded = false;

  ngOnInit(): void {
    this.update();

    interval(60_000)
      .pipe(
        takeUntil(this.destroy$),
        filter(() => !this.loading())
      )
      .subscribe(() => this.update());
  }

  update() {
    if (!this.reportId()) {
      return;
    }

    this.loading.set(true);

    this.phoneReportService
      .getTestResult(this.reportId())
      .pipe(
        catchError(error => {
          this.notificationService.error({
            title: 'Test results',
            message: 'An error occurred while loading the test result',
            serviceName: 'API GATEWAY',
            requestMessage: error.statusText,
            requestCode: error.status,
          });

          return EMPTY;
        }),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe(res => {
        this.rowData.set([res]);
      });
  }

  renderDate(result: TestResult): string {
    return TestResultUtils.renderDate(result as TestResultAction);
  }

  renderDlrStatus(result: TestResult): string {
    return DlrRecStatusRenderUtils.formatDlrRecStatus(result.testStatus, result.messageState, result.channel);
  }

  renderSender(result: TestResult): string {
    return TestResultUtils.renderSenderDelivered(result.senderDelivered, result.senderSent);
  }

  renderText(result: TestResult): string {
    return TestResultUtils.renderLntText(result.textDeliveredFull, result.textSentBackend);
  }

  renderSupplier(result: TestResult): string {
    return TestResultUtils.renderConnection(result as TestResultAction);
  }

  renderDelay(result: TestResult): string {
    return TestResultUtils.renderDelay(result.receiptDelay, result.smppDelay);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
